import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    Paper,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Divider,
    FormControlLabel,
    Checkbox,
    FormGroup,
    CircularProgress,
    Alert as MuiAlert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import { pollRegenJob, RegenJobError } from '../../utils/pollRegenJob';
import Alert from '../../components/alert/Alert';
import { Editor } from '@tinymce/tinymce-react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
// import ArchiveIcon from '@mui/icons-material/Archive'; // Commented out - no server endpoint
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/Upload';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TableChartIcon from '@mui/icons-material/TableChart';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Tooltip from '@mui/material/Tooltip';
import CompetitionUsageModal from './CompetitionUsageModal';
import CompetitionSearchModal from './CompetitionSearchModal';
import AdmissionNewsletterEditor from './AdmissionNewsletterEditor';

const NewsletterViewer = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { nid } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [loading, setLoading] = useState(true);
    const [newsletter, setNewsletter] = useState(null);
    const [viewMode, setViewMode] = useState('preview');

    // Edit fields (matching API structure)
    const [introText, setIntroText] = useState('');
    const [outroText, setOutroText] = useState('');
    const [themeColor, setThemeColor] = useState('#E67E22');
    const [competitions, setCompetitions] = useState([]);

    // Dialogs
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    // const [archiveDialogOpen, setArchiveDialogOpen] = useState(false); // Commented out - no server endpoint

    // MailerLite
    const [sendViaMailerLite, setSendViaMailerLite] = useState(false);
    const [mailerliteGroups, setMailerliteGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const [updatingHtml, setUpdatingHtml] = useState(false);

    // Alert
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState('');
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    // Whole-newsletter regeneration confirmation
    const [regenerateNewsletterDialogOpen, setRegenerateNewsletterDialogOpen] = useState(false);

    // Image regeneration
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const [imagePrompt, setImagePrompt] = useState('');
    const [regeneratingImage, setRegeneratingImage] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [savingImage, setSavingImage] = useState(false);

    // Custom image upload
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFilePreview, setSelectedFilePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Competition usage tracking
    const [competitionUsage, setCompetitionUsage] = useState({});
    const [usageModalOpen, setUsageModalOpen] = useState(false);
    const [selectedCompetition, setSelectedCompetition] = useState(null);

    // Competition search/replace
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [searchModalIndex, setSearchModalIndex] = useState(null);
    const [searchModalMode, setSearchModalMode] = useState('replace'); // 'replace' | 'add'
    const [titleEditWarningOpen, setTitleEditWarningOpen] = useState(false);
    const [titleEditWarningIndex, setTitleEditWarningIndex] = useState(null);
    const [titleWarningDismissed, setTitleWarningDismissed] = useState({});
    const [generatingAI, setGeneratingAI] = useState(null); // index of competition being generated

    // Competition deletion
    const [deleteCompetitionDialogOpen, setDeleteCompetitionDialogOpen] = useState(false);
    const [deleteCompetitionIndex, setDeleteCompetitionIndex] = useState(null);
    const [pendingDeleteOwnedIds, setPendingDeleteOwnedIds] = useState([]);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const fetchNewsletter = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setNewsletter(response.data);
            setIntroText(response.data.intro_text || '');
            setOutroText(response.data.outro_text || '');
            setThemeColor(response.data.theme_color || '#E67E22');
            setCompetitions(response.data.content_data?.competitions || []);
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                setErrorAlertMessage('Newsletter not found');
                setErrorAlertOpen(true);
                setTimeout(() => navigate('/admin/newsletters/competition'), 2000);
            } else {
                setErrorAlertMessage('Failed to load newsletter');
                setErrorAlertOpen(true);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Newsletters');
        fetchNewsletter();
    }, [nid]);

    // Fetch competition usage data
    const fetchCompetitionUsage = async () => {
        if (!nid) return;
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/competition-usage`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setCompetitionUsage(response.data.usage || {});
        } catch (error) {
            console.error('Failed to fetch competition usage:', error);
            // Non-critical error, don't show alert
        }
    };

    // Fetch usage when entering edit mode
    useEffect(() => {
        if (viewMode === 'edit' && newsletter?.competition_ids?.length > 0) {
            fetchCompetitionUsage();
        }
    }, [viewMode, newsletter?.competition_ids]);

    const handleSave = async () => {
        // Client-side guard mirroring the server validation (api/newsletters.js)
        if (competitions.length < 1 || competitions.length > 10) {
            setErrorAlertMessage('Newsletter must have between 1 and 10 competitions.');
            setErrorAlertOpen(true);
            return;
        }
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}`,
                {
                    intro_text: introText,
                    outro_text: outroText,
                    theme_color: themeColor,
                    competitions: competitions,
                    delete_owned_competition_ids: pendingDeleteOwnedIds,
                    status: 'draft'
                },
                { headers: { Authorization: getAuthHeader() } }
            );

            setPendingDeleteOwnedIds([]);
            setSuccessAlertMessage('Newsletter saved successfully');
            setSuccessAlertOpen(true);
            setViewMode('preview');
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to save newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    const fetchMailerLiteGroups = async () => {
        setLoadingGroups(true);
        setGroupsError(null);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/mailerlite/groups`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setMailerliteGroups(response.data.groups || []);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setGroupsError(error.response?.data?.error || 'Failed to load MailerLite groups');
            }
        } finally {
            setLoadingGroups(false);
        }
    };

    const handleOpenPublishDialog = () => {
        setPublishDialogOpen(true);
        setSendViaMailerLite(false);
        setSelectedGroupIds([]);
        fetchMailerLiteGroups();
    };

    const handleGroupToggle = (groupId) => {
        setSelectedGroupIds(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const handlePublish = async () => {
        setPublishing(true);
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/publish`,
                {
                    sendViaMailerLite,
                    groupIds: selectedGroupIds,
                },
                { headers: { Authorization: getAuthHeader() } }
            );

            let message = 'Newsletter published successfully';
            if (sendViaMailerLite && response.data.mailerlite) {
                if (response.data.mailerlite.status === 'sent') {
                    message = 'Newsletter published and sent via MailerLite!';
                } else if (response.data.mailerlite.status === 'error') {
                    message = `Published but MailerLite failed: ${response.data.mailerlite.message}`;
                }
            }

            setSuccessAlertMessage(message);
            setSuccessAlertOpen(true);
            setPublishDialogOpen(false);
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to publish newsletter');
                setErrorAlertOpen(true);
            }
        } finally {
            setPublishing(false);
        }
    };

    const handleUnpublish = async () => {
        setUnpublishing(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/unpublish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter unpublished successfully');
            setSuccessAlertOpen(true);
            setUnpublishDialogOpen(false);
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to unpublish newsletter');
                setErrorAlertOpen(true);
            }
        } finally {
            setUnpublishing(false);
        }
    };

    // Archive function - commented out (no server endpoint)
    // const handleArchive = async () => {
    //     try {
    //         await Axios.post(
    //             `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/archive`,
    //             {},
    //             { headers: { Authorization: getAuthHeader() } }
    //         );

    //         setSuccessAlertMessage('Newsletter archived successfully');
    //         setSuccessAlertOpen(true);
    //         setArchiveDialogOpen(false);
    //         navigate('/admin/newsletters/competition');
    //     } catch (error) {
    //         if (error.response?.status === 401) {
    //             handleLogout();
    //         } else {
    //             setErrorAlertMessage('Failed to archive newsletter');
    //             setErrorAlertOpen(true);
    //         }
    //     }
    // };

    const handleDelete = async () => {
        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}`,
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter deleted successfully');
            setSuccessAlertOpen(true);
            setDeleteDialogOpen(false);
            setTimeout(() => navigate('/admin/newsletters/competition'), 1000);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage('Failed to delete newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    const handleDownloadHTML = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/html`,
                {
                    headers: { Authorization: getAuthHeader() },
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${newsletter.title}.html`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setSuccessAlertMessage('HTML downloaded successfully');
            setSuccessAlertOpen(true);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage('Failed to download HTML');
                setErrorAlertOpen(true);
            }
        }
    };

    const handleUpdateHtml = async () => {
        setUpdatingHtml(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/regenerate-html`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('HTML template updated successfully');
            setSuccessAlertOpen(true);
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to update HTML');
                setErrorAlertOpen(true);
            }
        } finally {
            setUpdatingHtml(false);
        }
    };

    const handleRegenerate = async () => {
        setRegenerateNewsletterDialogOpen(false);
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/retry`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Newsletter regeneration started');
            setSuccessAlertOpen(true);
            setTimeout(() => {
                navigate(`/admin/newsletters/progress/${response.data.uuid}`);
            }, 1000);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage('Failed to regenerate newsletter');
                setErrorAlertOpen(true);
            }
        }
    };

    // Image regeneration functions
    const getDefaultImagePrompt = (month) => {
        const seasons = {
            12: 'winter', 1: 'winter', 2: 'winter',
            3: 'spring', 4: 'spring', 5: 'spring',
            6: 'summer', 7: 'summer', 8: 'summer',
            9: 'fall', 10: 'fall', 11: 'fall'
        };
        const season = seasons[month] || 'seasonal';
        return `A rich illustration or photo-style image that visually expresses ${season} seasonal feeling. The image should have vibrant but harmonious colors suitable for an educational newsletter header. Style: modern, clean, inspiring. No text or letters in the image.`;
    };

    const handleOpenRegenerateDialog = () => {
        setImagePrompt(getDefaultImagePrompt(newsletter.month));
        setRegenerateDialogOpen(true);
    };

    // Abort any in-flight hero-image regen polling loop on unmount.
    const heroAbortRef = useRef(null);
    useEffect(() => () => heroAbortRef.current?.abort(), []);

    const handleGeneratePreview = async () => {
        setRegeneratingImage(true);
        setRegenerateDialogOpen(false);

        try {
            // 202 { job_id } returns immediately; poll the background job so the
            // long image generation can't trip an upstream request timeout.
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/regenerate-hero-image`,
                { prompt: imagePrompt, preview: true },
                { headers: { Authorization: getAuthHeader() } }
            );

            const controller = new AbortController();
            heroAbortRef.current = controller;
            const result = await pollRegenJob(response.data.job_id, { signal: controller.signal });

            setPreviewImageUrl(result.hero_image_url);
            setPreviewDialogOpen(true);
        } catch (error) {
            if (error instanceof RegenJobError && error.code === 'ABORTED') {
                // Component unmounted — nothing to surface.
            } else if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || error.message || 'Failed to generate image');
                setErrorAlertOpen(true);
            }
        } finally {
            heroAbortRef.current = null;
            setRegeneratingImage(false);
        }
    };

    const handleSaveNewImage = async () => {
        if (!previewImageUrl) return;
        setSavingImage(true);

        try {
            // Commit the image the admin actually previewed — the preview already
            // generated + uploaded it to S3, so just persist that URL (no second
            // generation, which would produce a different image).
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/commit-hero-image`,
                { image_url: previewImageUrl },
                { headers: { Authorization: getAuthHeader() } }
            );

            setSuccessAlertMessage('Hero image updated successfully');
            setSuccessAlertOpen(true);
            setPreviewDialogOpen(false);
            setPreviewImageUrl(null);
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || error.message || 'Failed to save image');
                setErrorAlertOpen(true);
            }
        } finally {
            setSavingImage(false);
        }
    };

    const handleCancelPreview = () => {
        setPreviewDialogOpen(false);
        setPreviewImageUrl(null);
    };

    // Custom image upload handlers
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setErrorAlertMessage('Please select an image file');
                setErrorAlertOpen(true);
                return;
            }
            setSelectedFile(file);
            setSelectedFilePreview(URL.createObjectURL(file));
            setUploadDialogOpen(true);
        }
        // Reset input
        event.target.value = '';
    };

    const handleUploadImage = async () => {
        if (!selectedFile) return;

        setUploadingImage(true);

        try {
            const formData = new FormData();
            formData.append('image', selectedFile);

            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}/upload-hero-image`,
                formData,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setSuccessAlertMessage('Hero image updated successfully');
            setSuccessAlertOpen(true);
            setUploadDialogOpen(false);
            setSelectedFile(null);
            if (selectedFilePreview) {
                URL.revokeObjectURL(selectedFilePreview);
            }
            setSelectedFilePreview(null);
            fetchNewsletter();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.error || 'Failed to upload image');
                setErrorAlertOpen(true);
            }
        } finally {
            setUploadingImage(false);
        }
    };

    const handleCancelUpload = () => {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        if (selectedFilePreview) {
            URL.revokeObjectURL(selectedFilePreview);
        }
        setSelectedFilePreview(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'default';
            case 'published': return 'success';
            case 'archived': return 'default';
            default: return 'default';
        }
    };

    // Get source badge configuration
    const getSourceConfig = (comp) => {
        const usage = competitionUsage[comp.competition_id];

        if (comp.ai_recommended && !comp.competition_id) {
            return {
                icon: <AutoAwesomeIcon fontSize="small" />,
                label: 'AI Recommended',
                color: 'info'
            };
        }

        switch (usage?.source) {
            case 'ai_newsletter':
                return {
                    icon: <AutoAwesomeIcon fontSize="small" />,
                    label: 'AI Generated',
                    color: 'info'
                };
            case 'excel_import':
                return {
                    icon: <TableChartIcon fontSize="small" />,
                    label: 'Excel Import',
                    color: 'secondary'
                };
            case 'manual':
            default:
                return {
                    icon: <PersonIcon fontSize="small" />,
                    label: 'Database',
                    color: 'default'
                };
        }
    };

    // Get exclusion status configuration
    const getExclusionConfig = (comp) => {
        const usage = competitionUsage[comp.competition_id];

        if (!comp.competition_id) {
            return null; // AI recommended, not in DB yet
        }

        if (!usage?.used_at) {
            return {
                icon: <CheckCircleOutlineIcon fontSize="small" />,
                label: 'New',
                color: 'success'
            };
        }

        if (usage?.is_within_exclusion) {
            return {
                icon: <BlockIcon fontSize="small" />,
                label: `${usage.days_until_reusable}d`,
                color: 'warning'
            };
        }

        return {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Available',
            color: 'success'
        };
    };

    const handleOpenUsageModal = (comp) => {
        setSelectedCompetition(comp);
        setUsageModalOpen(true);
    };

    const handleReplaceCompetition = (index) => {
        setSearchModalMode('replace');
        setSearchModalIndex(index);
        setSearchModalOpen(true);
    };

    const handleAddCompetition = () => {
        setSearchModalMode('add');
        setSearchModalIndex(null);
        setSearchModalOpen(true);
    };

    const handleCompetitionSelected = async (competition, useAI = false) => {
        const mode = searchModalMode;
        const replaceIndex = searchModalIndex;
        let targetIndex = null;

        if (mode === 'replace' && replaceIndex !== null) {
            const updated = [...competitions];
            updated[replaceIndex] = {
                ...updated[replaceIndex],
                title: competition.name,
                competition_id: competition.id,
                ai_recommended: false,
            };
            setCompetitions(updated);
            targetIndex = replaceIndex;
        } else if (mode === 'add') {
            targetIndex = competitions.length;
            setCompetitions(prev => [
                ...prev,
                {
                    title: competition.name,
                    competition_id: competition.id,
                    ai_recommended: false,
                    description: '',
                    bullet_points: [''],
                    summary: '',
                },
            ]);
        }

        // If the picked competition was queued for owned-record deletion, clear that pending action
        // (the user is putting it back into the newsletter).
        if (competition.id != null) {
            setPendingDeleteOwnedIds(prev => prev.filter(id => id !== competition.id));
        }

        setSearchModalOpen(false);
        setSearchModalIndex(null);
        setSearchModalMode('replace');

        // If AI requested, generate content
        if (useAI && targetIndex !== null) {
            setGeneratingAI(targetIndex);
            try {
                const response = await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/competitions/${competition.id}/generate-newsletter-content`,
                    {},
                    { headers: { Authorization: getAuthHeader() } }
                );

                // Update with generated content
                setCompetitions(prev => {
                    const newComps = [...prev];
                    if (newComps[targetIndex]) {
                        newComps[targetIndex] = {
                            ...newComps[targetIndex],
                            description: response.data.description,
                            bullet_points: response.data.bullet_points,
                            summary: response.data.summary,
                        };
                    }
                    return newComps;
                });

                setSuccessAlertMessage('AI content generated successfully');
                setSuccessAlertOpen(true);
            } catch (error) {
                console.error('Failed to generate AI content:', error);
                setErrorAlertMessage(error.response?.data?.error || 'Failed to generate AI content');
                setErrorAlertOpen(true);
            } finally {
                setGeneratingAI(null);
            }
        }
    };

    const removeCompetitionAtIndex = (index, alsoDeleteSavedRecord = false) => {
        const comp = competitions[index];
        if (alsoDeleteSavedRecord && comp?.competition_id) {
            setPendingDeleteOwnedIds(prev =>
                prev.includes(comp.competition_id) ? prev : [...prev, comp.competition_id]
            );
        }
        setCompetitions(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteCompetitionClick = (index) => {
        const comp = competitions[index];
        const usage = comp?.competition_id ? competitionUsage[comp.competition_id] : null;
        if (usage?.owned_by_this_newsletter) {
            setDeleteCompetitionIndex(index);
            setDeleteCompetitionDialogOpen(true);
        } else {
            removeCompetitionAtIndex(index, false);
        }
    };

    const closeDeleteCompetitionDialog = () => {
        setDeleteCompetitionDialogOpen(false);
        setDeleteCompetitionIndex(null);
    };

    const confirmDeleteCompetition = (alsoDeleteSavedRecord) => {
        if (deleteCompetitionIndex !== null) {
            removeCompetitionAtIndex(deleteCompetitionIndex, alsoDeleteSavedRecord);
        }
        closeDeleteCompetitionDialog();
    };

    const handleTitleFocus = (index) => {
        // Only show warning if not already dismissed for this competition
        if (!titleWarningDismissed[index]) {
            setTitleEditWarningIndex(index);
            setTitleEditWarningOpen(true);
        }
    };

    const handleDismissTitleWarning = () => {
        if (titleEditWarningIndex !== null) {
            setTitleWarningDismissed(prev => ({ ...prev, [titleEditWarningIndex]: true }));
        }
        setTitleEditWarningOpen(false);
        setTitleEditWarningIndex(null);
    };

    const handleUseReplaceButton = () => {
        if (titleEditWarningIndex !== null) {
            handleReplaceCompetition(titleEditWarningIndex);
        }
        setTitleEditWarningOpen(false);
        setTitleEditWarningIndex(null);
    };

    const updateCompetition = (index, field, value) => {
        const updated = [...competitions];
        updated[index] = { ...updated[index], [field]: value };
        setCompetitions(updated);
    };

    const updateCompetitionBulletPoint = (compIndex, bulletIndex, value) => {
        const updated = [...competitions];
        const bulletPoints = [...updated[compIndex].bullet_points];
        bulletPoints[bulletIndex] = value;
        updated[compIndex] = { ...updated[compIndex], bullet_points: bulletPoints };
        setCompetitions(updated);
    };

    const addBulletPoint = (compIndex) => {
        const updated = [...competitions];
        const bulletPoints = [...updated[compIndex].bullet_points, ''];
        updated[compIndex] = { ...updated[compIndex], bullet_points: bulletPoints };
        setCompetitions(updated);
    };

    const removeBulletPoint = (compIndex, bulletIndex) => {
        const updated = [...competitions];
        const bulletPoints = updated[compIndex].bullet_points.filter((_, i) => i !== bulletIndex);
        updated[compIndex] = { ...updated[compIndex], bullet_points: bulletPoints };
        setCompetitions(updated);
    };

    if (loading) {
        return <Loading />;
    }

    if (!newsletter) {
        return (
            <Box m="20px" marginX={3} marginY={3}>
                <Header title="Newsletter Not Found" subtitle="" />
                <Typography>The newsletter you're looking for doesn't exist.</Typography>
            </Box>
        );
    }

    // Admission newsletters use a separate, simpler editor body. We keep the
    // viewer wrapper component (so routing/auth/loading are shared), but the
    // edit/preview UI inside is forked since the data shape is different.
    if (newsletter.type === 'admission') {
        return (
            <AdmissionNewsletterEditor
                newsletter={newsletter}
                onReload={fetchNewsletter}
            />
        );
    }

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/newsletters/competition')}
                sx={{ mb: 1 }}
            >
                Back to Newsletters
            </Button>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Header title={newsletter.title} subtitle={`${new Date(newsletter.createdAt).toLocaleDateString()}`} />
                    <Chip
                        label={newsletter.status.toUpperCase()}
                        color={getStatusColor(newsletter.status)}
                        size="small"
                        sx={{ ml: 2 }}
                    />
                </Box>

                <Box display="flex" gap={1}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_event, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        <ToggleButton value="preview">
                            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                            Preview
                        </ToggleButton>
                        <ToggleButton value="edit">
                            <EditIcon fontSize="small" sx={{ mr: 1 }} />
                            Edit
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {viewMode === 'preview' ? (
                <Paper sx={{ p: 2, backgroundColor: colors.primary[600] }}>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        {newsletter.status !== 'published' && (
                            <Button
                                variant="contained"
                                startIcon={<PublishIcon />}
                                onClick={handleOpenPublishDialog}
                                sx={{ backgroundColor: colors.greenAccent[600] }}
                            >
                                Publish
                            </Button>
                        )}

                        {newsletter.status === 'published' && (
                            <Button
                                variant="outlined"
                                startIcon={<UndoIcon />}
                                onClick={() => setUnpublishDialogOpen(true)}
                                color="warning"
                            >
                                Unpublish
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadHTML}
                        >
                            Download HTML
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={updatingHtml ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            onClick={handleUpdateHtml}
                            disabled={updatingHtml}
                        >
                            {updatingHtml ? 'Updating...' : 'Update HTML'}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={regeneratingImage ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
                            onClick={handleOpenRegenerateDialog}
                            disabled={regeneratingImage}
                        >
                            {regeneratingImage ? 'Generating...' : 'Regenerate Image'}
                        </Button>

                        {/* Hidden file input for image upload */}
                        <input
                            type="file"
                            id="hero-image-upload"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />

                        <Button
                            variant="outlined"
                            startIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                            onClick={() => document.getElementById('hero-image-upload').click()}
                            disabled={uploadingImage}
                        >
                            {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => setRegenerateNewsletterDialogOpen(true)}
                        >
                            Regenerate
                        </Button>

                        {/* Archive functionality - commented out (no server endpoint) */}
                        {/* {newsletter.status !== 'archived' && (
                            <Button
                                variant="outlined"
                                startIcon={<ArchiveIcon />}
                                onClick={() => setArchiveDialogOpen(true)}
                            >
                                Archive
                            </Button>
                        )} */}

                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Delete
                        </Button>

                        {/* AI Competition Save Button - pushed to the right */}
                        {newsletter.content_data?.competitions?.some(c => c.ai_recommended === true) && (
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={() => navigate(`/admin/newsletters/${nid}/save-ai-competitions`)}
                                sx={{
                                    marginLeft: 'auto',
                                    backgroundColor: colors.blueAccent[600],
                                    '&:hover': {
                                        backgroundColor: colors.blueAccent[700]
                                    }
                                }}
                            >
                                Save AI Competitions
                            </Button>
                        )}
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${colors.primary[500]}`,
                            borderRadius: 1,
                            overflow: 'auto',
                            backgroundColor: 'white'
                        }}
                    >
                        {newsletter.newsletter_html ? (
                            <iframe
                                srcDoc={newsletter.newsletter_html}
                                style={{
                                    width: '100%',
                                    height: '80vh',
                                    border: 'none'
                                }}
                                title="Newsletter Preview"
                            />
                        ) : (
                            <Box p={4} textAlign="center">
                                <Typography variant="h6" color={colors.grey[500]}>
                                    No HTML content available
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            ) : (
                <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        <TextField
                            label="Theme Color"
                            type="color"
                            value={themeColor}
                            onChange={(e) => setThemeColor(e.target.value)}
                            fullWidth
                            helperText="Select the theme color for this newsletter"
                        />

                        <Box>
                            <Typography variant="body2" mb={1} color={colors.grey[300]}>
                                Intro Text
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                value={introText}
                                onChange={(e) => setIntroText(e.target.value)}
                                fullWidth
                                placeholder="Enter the introduction text for the newsletter..."
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" mb={1} color={colors.grey[300]}>
                                Outro Text
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                value={outroText}
                                onChange={(e) => setOutroText(e.target.value)}
                                fullWidth
                                placeholder="Enter the closing text for the newsletter..."
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" mb={2} color={colors.grey[100]}>
                            Competitions ({competitions.length}/10, min 1)
                        </Typography>

                        {competitions.map((comp, index) => {
                            const sourceConfig = getSourceConfig(comp);
                            const exclusionConfig = getExclusionConfig(comp);
                            const usage = competitionUsage[comp.competition_id];

                            return (
                            <Accordion key={index} sx={{ mb: 2, backgroundColor: colors.primary[600] }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2, gap: 1 }}>
                                        <Typography sx={{ flexShrink: 0 }}>
                                            {index + 1}. {comp.title || 'Untitled Competition'}
                                        </Typography>

                                        {/* Replace Button - next to title */}
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<SwapHorizIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReplaceCompetition(index);
                                            }}
                                            sx={{ ml: 1, height: 24, fontSize: '0.75rem' }}
                                        >
                                            Replace
                                        </Button>

                                        {/* Delete Button */}
                                        <Tooltip title={competitions.length <= 1 ? 'At least 1 competition is required' : 'Remove this competition'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={competitions.length <= 1}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteCompetitionClick(index);
                                                    }}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        {/* AI Generation Loading Indicator */}
                                        {generatingAI === index && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="caption">Generating...</Typography>
                                            </Box>
                                        )}

                                        {/* Badges - pushed to the right */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                            {/* Source Badge */}
                                            <Tooltip title={`Source: ${sourceConfig.label}`}>
                                                <Chip
                                                    icon={sourceConfig.icon}
                                                    label={sourceConfig.label}
                                                    size="small"
                                                    color={sourceConfig.color}
                                                    variant="outlined"
                                                    sx={{ height: 24 }}
                                                />
                                            </Tooltip>

                                            {/* Exclusion Status Badge */}
                                            {exclusionConfig && (
                                                <Tooltip title={
                                                    exclusionConfig.color === 'warning'
                                                        ? `Within 4-month exclusion window. Available in ${usage?.days_until_reusable} days.`
                                                        : exclusionConfig.label === 'New'
                                                            ? 'Never used in a newsletter'
                                                            : 'Available for use in newsletters'
                                                }>
                                                    <Chip
                                                        icon={exclusionConfig.icon}
                                                        label={exclusionConfig.label}
                                                        size="small"
                                                        color={exclusionConfig.color}
                                                        sx={{ height: 24 }}
                                                    />
                                                </Tooltip>
                                            )}

                                            {/* History Button */}
                                            <Tooltip title="View usage history">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenUsageModal(comp);
                                                    }}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TextField
                                            label="Title"
                                            value={comp.title}
                                            onChange={(e) => updateCompetition(index, 'title', e.target.value)}
                                            onFocus={() => handleTitleFocus(index)}
                                            fullWidth
                                            size="small"
                                        />

                                        <TextField
                                            label="Description (2 sentences, 35-40 words)"
                                            multiline
                                            rows={2}
                                            value={comp.description}
                                            onChange={(e) => updateCompetition(index, 'description', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />

                                        <Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="body2">Bullet Points</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => addBulletPoint(index)}
                                                    color="primary"
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </Box>
                                            {comp.bullet_points?.map((point, bulletIndex) => (
                                                <Box key={bulletIndex} display="flex" gap={1} mb={1}>
                                                    <TextField
                                                        value={point}
                                                        onChange={(e) =>
                                                            updateCompetitionBulletPoint(index, bulletIndex, e.target.value)
                                                        }
                                                        fullWidth
                                                        size="small"
                                                        placeholder={`Bullet point ${bulletIndex + 1}`}
                                                    />
                                                    {comp.bullet_points.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeBulletPoint(index, bulletIndex)}
                                                            color="error"
                                                        >
                                                            <RemoveIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>

                                        <TextField
                                            label="Summary (25-30 words about benefits)"
                                            multiline
                                            rows={2}
                                            value={comp.summary}
                                            onChange={(e) => updateCompetition(index, 'summary', e.target.value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                            );
                        })}

                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddCompetition}
                                disabled={competitions.length >= 10}
                            >
                                Add Competition
                            </Button>
                        </Box>

                        <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setViewMode('preview');
                                    setIntroText(newsletter.intro_text || '');
                                    setOutroText(newsletter.outro_text || '');
                                    setThemeColor(newsletter.theme_color || '#E67E22');
                                    setCompetitions(newsletter.content_data?.competitions || []);
                                    setPendingDeleteOwnedIds([]);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                sx={{
                                    backgroundColor: colors.primary[300],
                                    color: 'white'
                                }}
                            >
                                Save Draft
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Publish Dialog */}
            <Dialog
                open={publishDialogOpen}
                onClose={() => !publishing && setPublishDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Publish Newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This will mark the newsletter as published.
                    </DialogContentText>

                    {/* Warning for unsaved AI competitions */}
                    {competitions.some(c => c.ai_recommended === true) && (
                        <MuiAlert
                            severity="warning"
                            sx={{ mb: 2 }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setPublishDialogOpen(false);
                                        navigate(`/admin/newsletters/${nid}/save-ai-competitions`);
                                    }}
                                >
                                    Save Now
                                </Button>
                            }
                        >
                            Some competitions are AI-generated and not saved to the database.
                            Consider saving them first for proper tracking.
                        </MuiAlert>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendViaMailerLite}
                                onChange={(e) => setSendViaMailerLite(e.target.checked)}
                                disabled={publishing}
                            />
                        }
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <SendIcon fontSize="small" />
                                <Typography>Send via MailerLite</Typography>
                            </Box>
                        }
                    />

                    {sendViaMailerLite && (
                        <Box sx={{ mt: 2, ml: 4 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                Select recipient groups (leave empty to send to all subscribers):
                            </Typography>

                            {loadingGroups ? (
                                <Box display="flex" alignItems="center" gap={1} py={2}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Loading groups...</Typography>
                                </Box>
                            ) : groupsError ? (
                                <MuiAlert severity="error" sx={{ mt: 1 }}>
                                    {groupsError}
                                </MuiAlert>
                            ) : mailerliteGroups.length === 0 ? (
                                <MuiAlert severity="info" sx={{ mt: 1 }}>
                                    No groups found. Newsletter will be sent to all subscribers.
                                </MuiAlert>
                            ) : (
                                <FormGroup>
                                    {mailerliteGroups.map((group) => (
                                        <FormControlLabel
                                            key={group.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedGroupIds.includes(group.id)}
                                                    onChange={() => handleGroupToggle(group.id)}
                                                    disabled={publishing}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {group.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {group.active_count} active subscribers
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            )}

                            {selectedGroupIds.length === 0 && !loadingGroups && !groupsError && mailerliteGroups.length > 0 && (
                                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                                    No groups selected - will send to ALL subscribers
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPublishDialogOpen(false)}
                        disabled={publishing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePublish}
                        variant="contained"
                        color="success"
                        disabled={publishing}
                        startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    >
                        {publishing ? 'Publishing...' : (sendViaMailerLite ? 'Publish & Send' : 'Publish')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unpublish Dialog */}
            <Dialog open={unpublishDialogOpen} onClose={() => !unpublishing && setUnpublishDialogOpen(false)}>
                <DialogTitle>Unpublish Newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to unpublish this newsletter? This will revert the status back to "completed".
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnpublishDialogOpen(false)} disabled={unpublishing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUnpublish}
                        variant="contained"
                        color="warning"
                        disabled={unpublishing}
                        startIcon={unpublishing ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                    >
                        {unpublishing ? 'Unpublishing...' : 'Unpublish'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Archive Dialog - commented out (no server endpoint) */}
            {/* <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
                <DialogTitle>Archive Newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to archive this newsletter?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleArchive} variant="contained" color="warning">
                        Archive
                    </Button>
                </DialogActions>
            </Dialog> */}

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Newsletter</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this newsletter? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Regenerate Whole Newsletter Confirmation */}
            <Dialog
                open={regenerateNewsletterDialogOpen}
                onClose={() => setRegenerateNewsletterDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Regenerate Newsletter?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This restarts the entire newsletter generation from scratch.
                        The current content will be replaced and you'll be taken to the
                        progress page. This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenerateNewsletterDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRegenerate}
                        variant="contained"
                        color="warning"
                        startIcon={<RefreshIcon />}
                    >
                        Regenerate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Regenerate Image Dialog - Prompt Edit */}
            <Dialog
                open={regenerateDialogOpen}
                onClose={() => setRegenerateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Regenerate Hero Image</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Edit the prompt below to customize the generated image.
                        The image model works best with English prompts.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Image Prompt"
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                        placeholder="Describe the image you want..."
                        helperText={`${imagePrompt.length}/1000 characters`}
                        inputProps={{ maxLength: 1000 }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenerateDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGeneratePreview}
                        variant="contained"
                        disabled={!imagePrompt.trim()}
                        startIcon={<ImageIcon />}
                    >
                        Generate Preview
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Comparison Dialog */}
            <Dialog
                open={previewDialogOpen}
                onClose={() => !savingImage && handleCancelPreview()}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Compare Images</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Compare the current image with the newly generated image.
                    </DialogContentText>
                    <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {/* Current Image */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Current</Typography>
                            <Box
                                component="img"
                                src={newsletter?.hero_image_url}
                                alt="Current"
                                sx={{
                                    width: 256, height: 256,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'grey.300',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                        {/* New Image */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>New</Typography>
                            <Box
                                component="img"
                                src={previewImageUrl}
                                alt="New"
                                sx={{
                                    width: 256, height: 256,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'success.main',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPreview} disabled={savingImage}>
                        Keep Current
                    </Button>
                    <Button
                        onClick={handleSaveNewImage}
                        variant="contained"
                        color="success"
                        disabled={savingImage}
                        startIcon={savingImage ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {savingImage ? 'Saving...' : 'Use New Image'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Image Preview Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={() => !uploadingImage && handleCancelUpload()}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Upload Custom Image</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Preview your image before uploading. It will be resized to 512x512 and converted to WebP.
                    </DialogContentText>
                    <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {/* Current Image */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Current</Typography>
                            <Box
                                component="img"
                                src={newsletter?.hero_image_url}
                                alt="Current"
                                sx={{
                                    width: 256, height: 256,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'grey.300',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                        {/* New Image */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>New</Typography>
                            <Box
                                component="img"
                                src={selectedFilePreview}
                                alt="New"
                                sx={{
                                    width: 256, height: 256,
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'success.main',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                    </Box>
                    {selectedFile && (
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                            File: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelUpload} disabled={uploadingImage}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUploadImage}
                        variant="contained"
                        color="success"
                        disabled={uploadingImage}
                        startIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    >
                        {uploadingImage ? 'Uploading...' : 'Use This Image'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading overlay during image generation */}
            {regeneratingImage && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white' }}>
                        Generating new image...
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'grey.300', mt: 1 }}>
                        This may take up to 30 seconds
                    </Typography>
                </Box>
            )}

            {/* Competition Usage Modal */}
            <CompetitionUsageModal
                open={usageModalOpen}
                onClose={() => setUsageModalOpen(false)}
                competition={selectedCompetition}
                usageData={competitionUsage[selectedCompetition?.competition_id]}
            />

            {/* Competition Search/Replace/Add Modal */}
            <CompetitionSearchModal
                open={searchModalOpen}
                onClose={() => {
                    setSearchModalOpen(false);
                    setSearchModalIndex(null);
                    setSearchModalMode('replace');
                }}
                onSelect={handleCompetitionSelected}
                excludeIds={competitions
                    .filter(c => c.competition_id != null)
                    .map(c => c.competition_id)
                }
            />

            {/* Title Edit Warning Dialog */}
            <Dialog
                open={titleEditWarningOpen}
                onClose={() => {
                    setTitleEditWarningOpen(false);
                    setTitleEditWarningIndex(null);
                }}
            >
                <DialogTitle>Change Competition?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        If you want to replace this competition with another one from the database,
                        please use the <strong>Replace</strong> button instead. This ensures the
                        competition ID is properly updated for tracking purposes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDismissTitleWarning}>
                        Edit Title Manually
                    </Button>
                    <Button
                        onClick={handleUseReplaceButton}
                        variant="contained"
                        startIcon={<SwapHorizIcon />}
                    >
                        Use Replace
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Owned Competition Confirmation */}
            <Dialog
                open={deleteCompetitionDialogOpen}
                onClose={closeDeleteCompetitionDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Remove Competition</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This competition was saved to the database when this newsletter was generated.
                        Choose what to do with the saved record.
                    </DialogContentText>
                    {(() => {
                        if (deleteCompetitionIndex === null) return null;
                        const comp = competitions[deleteCompetitionIndex];
                        const usage = comp?.competition_id ? competitionUsage[comp.competition_id] : null;
                        const otherNewsletters = (usage?.newsletter_history || []).filter(
                            n => n.uuid !== newsletter?.uuid
                        );
                        if (otherNewsletters.length === 0) return null;
                        return (
                            <MuiAlert severity="warning">
                                This competition is also referenced by {otherNewsletters.length} other
                                published newsletter{otherNewsletters.length === 1 ? '' : 's'}.
                                Choosing "Also delete saved record" will orphan it from those newsletters.
                            </MuiAlert>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteCompetitionDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => confirmDeleteCompetition(false)}
                        variant="outlined"
                    >
                        Remove from newsletter only
                    </Button>
                    <Button
                        onClick={() => confirmDeleteCompetition(true)}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Also delete saved record
                    </Button>
                </DialogActions>
            </Dialog>

            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical='bottom'
                horizontal='left'
            />
        </Box>
    );
};

export default NewsletterViewer;
